import { useState, useMemo } from 'react';
import { BookOpen, Search, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { learningPaths as footballLearningPaths } from '@/data/lessons';
import { basketballLearningPaths } from '@/data/basketballLessons';
import { soccerLearningPaths } from '@/data/soccerLessons';
import { baseballLearningPaths } from '@/data/baseballLessons';
import type { LearningPath, Lesson } from '@/data/lessons';

interface LessonPickerProps {
  sport: string;
  selectedLessonIds: string[];
  onToggleLesson: (lessonId: string) => void;
  onAssign?: () => void;
  assignLabel?: string;
  persistedCount?: number;
  assignmentLimit?: number;
}

function getLessonsForSport(sport: string): { paths: LearningPath[]; allLessons: Lesson[] } {
  const paths = sport === 'basketball'
    ? basketballLearningPaths
    : sport === 'soccer'
      ? soccerLearningPaths
      : sport === 'baseball'
        ? baseballLearningPaths
        : footballLearningPaths;

  const allLessons = paths.flatMap(p => p.units.flatMap(u => u.lessons));
  return { paths, allLessons };
}

export default function LessonPicker({
  sport,
  selectedLessonIds,
  onToggleLesson,
  onAssign,
  assignLabel = 'Assign',
  persistedCount,
  assignmentLimit,
}: LessonPickerProps) {
  const [search, setSearch] = useState('');
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());

  const { paths } = getLessonsForSport(sport);

  const filteredPaths = useMemo(() => {
    if (!search.trim()) return paths;
    const q = search.toLowerCase();
    return paths.map(path => ({
      ...path,
      units: path.units.map(unit => ({
        ...unit,
        lessons: unit.lessons.filter(l =>
          l.title.toLowerCase().includes(q) || l.description.toLowerCase().includes(q)
        ),
      })).filter(u => u.lessons.length > 0),
    })).filter(p => p.units.length > 0);
  }, [paths, search]);

  const togglePath = (pathId: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      next.has(pathId) ? next.delete(pathId) : next.add(pathId);
      return next;
    });
  };

  return (
    <div className="space-y-3">
      {onAssign && (
        <div className="flex items-center justify-between">
          <span className="text-xs font-display font-bold text-primary">
            {selectedLessonIds.length} lesson{selectedLessonIds.length === 1 ? '' : 's'} selected
          </span>
          <span className="text-[10px] text-muted-foreground font-body">
            {persistedCount == null
              ? 'Tap to check / uncheck'
              : `${persistedCount}${assignmentLimit === -1 ? '' : ` of ${assignmentLimit}`} active`}
          </span>
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search lessons..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 font-body"
        />
      </div>

      <div className="max-h-64 overflow-y-auto space-y-1">
        {filteredPaths.map(path => (
          <div key={path.id}>
            <button
              onClick={() => togglePath(path.id)}
              className="w-full flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-muted/50 text-left"
            >
              {expandedPaths.has(path.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <span className="font-display font-bold text-sm">{path.title}</span>
              <span className="text-[10px] text-muted-foreground capitalize">{path.difficulty}</span>
            </button>
            {expandedPaths.has(path.id) && path.units.map(unit => (
              <div key={unit.id} className="ml-4">
                <p className="text-xs font-display font-bold text-muted-foreground py-1 flex items-center gap-1">
                  {unit.icon} {unit.title}
                </p>
                {unit.lessons.map(lesson => {
                  const selected = selectedLessonIds.includes(lesson.id);
                  return (
                    <button
                      key={lesson.id}
                      onClick={() => onToggleLesson(lesson.id)}
                      className={`w-full flex items-center gap-2 py-1.5 px-3 ml-2 rounded-lg text-left text-sm transition-colors ${
                        selected ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50 text-foreground'
                      }`}
                    >
                      {selected ? <Check className="w-3.5 h-3.5 text-primary shrink-0" /> : <BookOpen className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                      <span className="font-body truncate">{lesson.title}</span>
                      <span className="text-[10px] text-muted-foreground ml-auto shrink-0">{lesson.xpReward} XP</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>

      {onAssign && (
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <span className="text-xs text-muted-foreground font-body">
            {selectedLessonIds.length} lesson{selectedLessonIds.length === 1 ? '' : 's'} selected
          </span>
          <Button size="sm" onClick={onAssign} className="font-display font-bold">
            {assignLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export { getLessonsForSport };
